/**
 * Universal billing checkout dialog — handles BOTH plan upgrades and storage
 * pack purchases through a single UX. Caller passes a `sku` describing what's
 * being sold and which API to hit; the dialog runs a phase machine that
 * adapts to PIX (QR display), Cartão (tokenize → submit) and Boleto.
 *
 * Phase machine:
 *
 *   info  ─PIX/Boleto─▶ submitting ─▶ pix_qr | boleto_url
 *      └─Cartão─▶ ensure_customer ─▶ card_form ─▶ submitting ─▶ success
 *
 * Mais de uma cobrança? Cada SKU é independente — abre nova diálogo.
 *
 * Por que não escondemos PIX atrás de "modo avançado": PIX no Brasil é
 * dominante pra primeira compra (quem não tem cartão salvo confia mais
 * num QR que ele já reconhece). Cartão fica em pé de igualdade no seletor
 * pra quem quer 1-clique pelas próximas (a partir do Lote 10C, com vault).
 */
import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Check,
  Loader2,
  Copy,
  QrCode as QrCodeIcon,
  Sparkles,
  Zap,
  Crown,
  HardDrive,
  ArrowRight,
  ArrowLeft,
  ExternalLink,
  CreditCard as CreditCardIcon,
  Banknote,
  Ticket,
  X as XIcon,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ResponsiveDialog } from '@/components/ui/responsive-dialog';
import { BrandBar } from '@/components/ui/BrandLoader';
import { Input } from '@/components/ui/input';
import { FormField } from '@/components/ui/form-field';
import { useToast } from '@/components/ui/toast';
import { useAuthStore } from '@/store/authStore';
import { api } from '@/lib/api';
import { formatPriceBRL, formatStorage } from '@my360/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useBillingSocket } from '@/hooks/useBillingSocket';
import { useValidateCoupon } from '@/api/queries';
import { purchasesAllowed } from '@/lib/purchases';
import { CreditCardForm } from './CreditCardForm';

export type BillingSku =
  | {
      kind: 'plan';
      slug: string;
      name: string;
      priceCents: number;
      tagline?: string;
    }
  | {
      kind: 'pack';
      slug: string;
      name: string;
      priceCents: number;
      bonusBytes: number;
    };

interface CheckoutResponse {
  status?: string;
  billingType?: string;
  pixQrCode?: string;
  pixCopyPaste?: string;
  pixExpiration?: string;
  paymentId?: string;
  invoiceUrl?: string;
  bankSlipUrl?: string;
  subscriptionId?: string;
  asaasSubscriptionId?: string;
  plan?: string;
}

interface BillingCheckoutDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sku: BillingSku | null;
  onSuccess?: (resp: CheckoutResponse) => void;
}

const SKU_ICON: Record<string, React.ElementType> = {
  free: Sparkles,
  pro: Zap,
  enterprise: Crown,
  beta: Sparkles,
};

type Phase =
  | 'info'           // form de dados pessoais + escolha do método
  | 'card_form'      // form do cartão (após ensure-customer)
  | 'submitting'     // request em voo (curto)
  | 'pix_qr'         // PIX gerado, QR exibido, aguardando pagamento (WS fecha)
  | 'boleto_url'     // boleto gerado, link disponível, aguardando compensação (WS fecha)
  | 'cc_processing'  // cartão tokenizado e enviado, aguardando autorização do banco (WS fecha)
  | 'confirmed';     // webhook chegou, mostra success por 1.5s e fecha

/**
 * Backstop de compliance: no app nativo (Google Play) nenhum checkout
 * pode existir — mesmo que algum caminho esquecido tente abrir este
 * dialog, ele não renderiza. O gate fica no wrapper (e não num early
 * return interno) pra não condicionar a ordem dos hooks.
 */
export function BillingCheckoutDialog(props: BillingCheckoutDialogProps) {
  if (!purchasesAllowed()) return null;
  return <BillingCheckoutDialogInner {...props} />;
}

function BillingCheckoutDialogInner({
  open,
  onOpenChange,
  sku,
  onSuccess,
}: BillingCheckoutDialogProps) {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [phase, setPhase] = useState<Phase>('info');
  const [form, setForm] = useState({
    name: user?.name ?? '',
    email: user?.email ?? '',
    cpfCnpj: '',
    phone: '',
    billingType: 'PIX' as 'PIX' | 'BOLETO' | 'CREDIT_CARD',
  });
  const [asaasCustomerId, setAsaasCustomerId] = useState<string | null>(null);
  const [pixResult, setPixResult] = useState<{
    pixQrCode?: string;
    pixCopyPaste?: string;
    paymentId?: string;
  } | null>(null);
  const [boletoUrl, setBoletoUrl] = useState<string | null>(null);
  const [pixCopied, setPixCopied] = useState(false);

  // ── Coupon / invite code (plans AND storage packs — packs need an
  //    explicit 'pack:<slug>' listing in the coupon's applies_to) ──
  const [couponInput, setCouponInput] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState<{
    code: string;
    priceCents: number;
    description: string | null;
  } | null>(null);
  const validateCouponMutation = useValidateCoupon();

  // Track which SKU is in-flight so the WS auto-close fires for the right tx
  const inFlightSkuRef = useRef<{ kind: 'plan' | 'pack'; slug: string } | null>(null);

  // Reset state when dialog reopens with a different sku
  useEffect(() => {
    if (open && sku) {
      setPhase('info');
      setPixResult(null);
      setBoletoUrl(null);
      setPixCopied(false);
      setCouponInput('');
      setAppliedCoupon(null);
    }
    if (!open) {
      inFlightSkuRef.current = null;
    }
  }, [open, sku?.slug, sku?.kind]);

  // WS — toda fase em que estamos AGUARDANDO confirmação do gateway.
  // Quando chega o webhook PAYMENT_CONFIRMED (real, do Asaas), virá pra
  // 'confirmed' por 1.5s e depois fecha. Antes do webhook a UI nunca
  // afirma que ativou — fica honesta ao estado real.
  const isAwaitingConfirmation =
    phase === 'pix_qr' || phase === 'boleto_url' || phase === 'cc_processing';

  useBillingSocket({
    enabled: open && isAwaitingConfirmation,
    onPaymentConfirmed: (plan) => {
      const expected = inFlightSkuRef.current;
      if (expected?.kind === 'plan' && expected.slug === plan) {
        setPhase('confirmed');
        toast.success(t('billingUi:checkout.toast.planActivated'), t('billingUi:checkout.toast.paymentConfirmed'));
        setTimeout(() => onOpenChange(false), 1500);
      }
    },
    onStoragePackActivated: (packSlug) => {
      const expected = inFlightSkuRef.current;
      if (expected?.kind === 'pack' && expected.slug === packSlug) {
        setPhase('confirmed');
        // useBillingSocket já toasta "Pack ativado" — só transiciona estado
        setTimeout(() => onOpenChange(false), 1500);
      }
    },
    onStoragePackFailed: (packSlug) => {
      const expected = inFlightSkuRef.current;
      if (expected?.kind === 'pack' && expected.slug === packSlug) {
        // Falha real — useBillingSocket toasta o erro — fecha pro user retentar
        onOpenChange(false);
      }
    },
  });

  // PIX polling fallback — SEMPRE ativo (não só localhost). WebSocket
  // é o caminho primário (latência ~1s após webhook), mas se webhook
  // Asaas demorar / falhar / não estiver registrado, o polling garante
  // que o modal fecha quando o pagamento confirma. Intervalo 3s é
  // tranquilo em carga (1-2 polls por minuto até confirmar). Era
  // localhost-only antes — usuário em prod ficava travado se webhook
  // não chegasse.
  useEffect(() => {
    if (phase !== 'pix_qr' || !pixResult?.paymentId) return;

    const paymentId = pixResult.paymentId;
    let stopped = false;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const poll = async () => {
      if (stopped) return;
      try {
        const r = await api.get<{ success: boolean; data?: { changed: boolean } }>(
          `/billing/poll-payment/${paymentId}`,
        );
        if (!stopped && r.success && r.data?.changed) {
          await Promise.all([
            queryClient.invalidateQueries({ queryKey: ['billing'] }),
            queryClient.invalidateQueries({ queryKey: ['storage-packs', 'my'] }),
            queryClient.invalidateQueries({ queryKey: ['projects'] }),
          ]);
          // Confirmação real (mesmo caminho do WS path)
          setPhase('confirmed');
          toast.success(
            sku?.kind === 'pack' ? t('billingUi:checkout.toast.packActivated') : t('billingUi:checkout.toast.planActivated'),
            t('billingUi:checkout.toast.paymentConfirmed'),
          );
          setTimeout(() => onOpenChange(false), 1500);
          return;
        }
      } catch {
        // retry next tick
      }
      if (!stopped) timeoutId = setTimeout(poll, 3000);
    };

    timeoutId = setTimeout(poll, 2000);

    return () => {
      stopped = true;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [phase, pixResult?.paymentId, queryClient, sku?.kind, onOpenChange, toast]);

  if (!sku) return null;

  // Preço efetivo — cupom aplicado sobrescreve o preço do plano/pack (a
  // assinatura recorrente na Asaas nasce com esse valor).
  const effectivePriceCents = appliedCoupon ? appliedCoupon.priceCents : sku.priceCents;

  const handleApplyCoupon = () => {
    if (!couponInput.trim()) return;
    validateCouponMutation.mutate(
      sku.kind === 'pack'
        ? { code: couponInput, pack: sku.slug }
        : { code: couponInput, plan: sku.slug },
      {
        onSuccess: (res) => {
          if (res.data?.valid) {
            setAppliedCoupon({
              code: couponInput.trim().toUpperCase(),
              priceCents: res.data.priceCents,
              description: res.data.description ?? null,
            });
            toast.success(
              t('billingUi:checkout.coupon.appliedTitle'),
              t('billingUi:checkout.coupon.appliedDesc', {
                code: couponInput.trim().toUpperCase(),
              }),
            );
          } else {
            toast.error(t('billingUi:checkout.coupon.invalid'));
          }
        },
        onError: (err) => {
          const msg = err instanceof Error ? err.message : '';
          toast.error(t('billingUi:checkout.coupon.invalid'), msg || undefined);
        },
      },
    );
  };

  const Icon = sku.kind === 'pack' ? HardDrive : SKU_ICON[sku.slug] || Zap;
  const subtitle =
    sku.kind === 'plan'
      ? sku.tagline || t('billingUi:checkout.planSubtitle', { price: formatPriceBRL(sku.priceCents) })
      : t('billingUi:checkout.packSubtitle', { price: formatPriceBRL(sku.priceCents) });

  const titleByPhase: Record<Phase, { title: string; sub: string; icon: React.ReactNode }> = {
    info: {
      title: sku.kind === 'pack' ? t('billingUi:checkout.phase.infoBuyTitle', { name: sku.name }) : t('billingUi:checkout.phase.infoSubscribeTitle', { name: sku.name }),
      sub: subtitle,
      icon: <Icon className="h-4 w-4" />,
    },
    card_form: {
      title: t('billingUi:checkout.phase.cardFormTitle'),
      sub: t('billingUi:checkout.phase.cardFormSub'),
      icon: <CreditCardIcon className="h-4 w-4" />,
    },
    submitting: {
      title: t('billingUi:checkout.phase.submittingTitle'),
      sub: t('billingUi:checkout.phase.submittingSub'),
      icon: <Loader2 className="h-4 w-4 animate-spin" />,
    },
    pix_qr: {
      title: t('billingUi:checkout.phase.pixQrTitle'),
      sub: t('billingUi:checkout.phase.pixQrSub'),
      icon: <QrCodeIcon className="h-4 w-4" />,
    },
    boleto_url: {
      title: t('billingUi:checkout.phase.boletoTitle'),
      sub: t('billingUi:checkout.phase.boletoSub'),
      icon: <Banknote className="h-4 w-4" />,
    },
    cc_processing: {
      title: t('billingUi:checkout.phase.ccProcessingTitle'),
      sub: t('billingUi:checkout.phase.ccProcessingSub'),
      icon: <Loader2 className="h-4 w-4 animate-spin" />,
    },
    confirmed: {
      title: sku.kind === 'pack' ? t('billingUi:checkout.toast.packActivated') : t('billingUi:checkout.toast.planActivated'),
      sub: t('billingUi:checkout.toast.paymentConfirmed'),
      icon: <Check className="h-4 w-4" />,
    },
  };

  const handleClose = () => {
    if (phase === 'submitting') return;
    onOpenChange(false);
  };

  // Step 1 → next: ou submete direto (PIX/Boleto) ou parte pro card-form (Cartão)
  const handleInfoNext = async () => {
    if (!form.cpfCnpj.trim() || !form.name.trim() || !form.email.trim()) {
      toast.error(t('billingUi:checkout.toast.fillRequiredFields'));
      return;
    }

    if (form.billingType === 'CREDIT_CARD') {
      // Garante que o customerId existe — Asaas.js precisa dele pra tokenizar
      setPhase('submitting');
      try {
        const r = await api.request<{ data: { customerId: string } }>(
          '/billing/ensure-customer',
          {
            method: 'POST',
            body: {
              name: form.name,
              email: form.email,
              cpfCnpj: form.cpfCnpj.replace(/\D/g, ''),
              phone: form.phone || undefined,
            },
          },
        );
        setAsaasCustomerId(r.data.customerId);
        setPhase('card_form');
      } catch (err: unknown) {
        const e = err as { message?: string };
        toast.error(e.message || t('billingUi:checkout.toast.customerFailed'));
        setPhase('info');
      }
      return;
    }

    // PIX / Boleto — submete direto
    await submitCheckout();
  };

  const submitCheckout = async (token?: string) => {
    setPhase('submitting');
    try {
      const endpoint = sku.kind === 'pack' ? '/storage-packs/checkout' : '/billing/checkout';
      const baseBody = {
        billingType: form.billingType,
        name: form.name,
        email: form.email,
        cpfCnpj: form.cpfCnpj,
        phone: form.phone || undefined,
        ...(token && { creditCardToken: token }),
      };
      const body =
        sku.kind === 'pack'
          ? {
              ...baseBody,
              packSlug: sku.slug,
              ...(appliedCoupon && { couponCode: appliedCoupon.code }),
            }
          : {
              ...baseBody,
              plan: sku.slug,
              ...(appliedCoupon && { couponCode: appliedCoupon.code }),
            };

      const res = await api.request<{ data: CheckoutResponse }>(endpoint, {
        method: 'POST',
        body,
      });
      const data = res.data ?? {};

      // Cada método tem o seu state final esperado. Se o backend não devolveu
      // o que esperamos (ex: PIX sem copy-paste), cai no catch genérico.
      // Não toastamos "recebido" aqui — o toast de confirmação real só sai
      // quando o webhook do Asaas dispara via WS (useBillingSocket).

      if (form.billingType === 'PIX') {
        if (!data.pixCopyPaste) {
          throw new Error(t('billingUi:checkout.toast.pixFailed'));
        }
        inFlightSkuRef.current = { kind: sku.kind, slug: sku.slug };
        setPixResult({
          pixQrCode: data.pixQrCode,
          pixCopyPaste: data.pixCopyPaste,
          paymentId: data.paymentId,
        });
        setPhase('pix_qr');
        // Sem toast — modal aberto com QR já é o feedback visual.
      } else if (form.billingType === 'BOLETO') {
        if (!data.bankSlipUrl && !data.invoiceUrl) {
          throw new Error(t('billingUi:checkout.toast.boletoFailed'));
        }
        inFlightSkuRef.current = { kind: sku.kind, slug: sku.slug };
        setBoletoUrl(data.bankSlipUrl || data.invoiceUrl || null);
        setPhase('boleto_url');
        // Sem toast — modal aberto com botão "Abrir boleto" já é o feedback.
      } else if (form.billingType === 'CREDIT_CARD') {
        // Cartão tokenizado: Asaas autoriza imediatamente. Modal fica
        // aguardando o webhook PAYMENT_CONFIRMED chegar (WS) — só ali a
        // gente fecha e mostra "Pagamento confirmado!". Antes do webhook
        // a gente NÃO afirma que ativou.
        inFlightSkuRef.current = { kind: sku.kind, slug: sku.slug };
        setPhase('cc_processing');
        // Modal NÃO fecha aqui — espera o WS (useBillingSocket abaixo).
      }

      // Refresh caches independente do método
      queryClient.invalidateQueries({ queryKey: ['billing'] });
      queryClient.invalidateQueries({ queryKey: ['storage-packs', 'my'] });
      queryClient.invalidateQueries({ queryKey: ['projects'] });

      // onSuccess refresca dados do parent — não dispara toast (o toast
      // de confirmação real sai do WS handler do parent).
      onSuccess?.(data);
    } catch (err: unknown) {
      const e = err as { message?: string };
      toast.error(e.message || t('billingUi:checkout.toast.paymentFailed'));
      // Volta pra fase apropriada pro user ajustar
      setPhase(form.billingType === 'CREDIT_CARD' ? 'card_form' : 'info');
    }
  };

  const handleCardTokenized = (token: string) => {
    void submitCheckout(token);
  };

  const copyPix = async () => {
    if (!pixResult?.pixCopyPaste) return;
    await navigator.clipboard.writeText(pixResult.pixCopyPaste);
    setPixCopied(true);
    toast.success(t('billingUi:checkout.toast.pixCopied'));
    setTimeout(() => setPixCopied(false), 2000);
  };

  const headerInfo = titleByPhase[phase];

  // Footer adapta por fase. Era useMemo antes, mas o componente tem um early
  // return `if (!sku) return null` ANTES desse hook — fazia hooks aparecerem
  // condicionalmente em renders sucessivos quando sku trocava de null pra
  // definido, violando Rules of Hooks ("Rendered more hooks than during the
  // previous render"). useMemo aqui era overkill: render barata, sem ganho
  // mensurável. Voltou pra const calculada inline.
  const footer = (() => {
    // PIX e Boleto: usuário PODE fechar pra pagar depois — confirmamos via
    // WS/polling de qualquer jeito. Cartão: NÃO pode fechar enquanto
    // aguarda autorização (sem botão close).
    if (phase === 'pix_qr' || phase === 'boleto_url') {
      return (
        <Button variant="outline" onClick={handleClose} className="ml-auto">
          {t('billingUi:checkout.footer.closeConfirmLater')}
        </Button>
      );
    }
    if (phase === 'cc_processing' || phase === 'submitting') {
      return null; // sem footer — usuário não fecha durante autorização
    }
    if (phase === 'confirmed') {
      return (
        <Button variant="primary" onClick={handleClose} className="ml-auto">
          {t('billingUi:checkout.footer.ok')}
        </Button>
      );
    }
    if (phase === 'card_form') {
      return (
        <Button
          variant="outline"
          onClick={() => setPhase('info')}
          className="ml-auto"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('billingUi:checkout.footer.back')}
        </Button>
      );
    }
    // info
    return (
      <>
        <Button variant="outline" onClick={handleClose}>
          {t('billingUi:checkout.footer.cancel')}
        </Button>
        <Button
          variant="primary"
          onClick={handleInfoNext}
          disabled={
            !form.cpfCnpj.trim() || !form.name.trim() || !form.email.trim()
          }
        >
          {form.billingType === 'CREDIT_CARD' ? (
            <>
              {t('billingUi:checkout.footer.continue')}
              <ArrowRight className="h-4 w-4 ml-2" />
            </>
          ) : (
            <>
              <Check className="h-4 w-4 mr-2" />
              {t('billingUi:checkout.footer.confirm')}
            </>
          )}
        </Button>
      </>
    );
  })();

  return (
    <ResponsiveDialog
      open={open}
      onOpenChange={(o) => !o && handleClose()}
      title={
        <span className="flex items-center gap-3">
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-[var(--color-primary)] to-[var(--color-accent)] text-white shadow-md">
            {headerInfo.icon}
          </span>
          <span className="flex flex-col">
            <span
              className="text-base tracking-tight font-semibold"
              style={{ color: 'var(--color-text)' }}
            >
              {headerInfo.title}
            </span>
            <span
              className="text-[11px] font-normal"
              style={{ color: 'var(--color-text-muted)' }}
            >
              {headerInfo.sub}
            </span>
          </span>
        </span>
      }
      size="sm"
      footer={footer}
    >
      <AnimatePresence mode="wait">
        {phase === 'info' && (
          <motion.div
            key="info"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            className="space-y-4"
          >
            <div className="grid grid-cols-2 gap-3">
              <FormField label={t('billingUi:checkout.form.name')} htmlFor="checkout-name">
                <Input
                  id="checkout-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder={t('billingUi:checkout.form.namePlaceholder')}
                />
              </FormField>
              <FormField label={t('billingUi:checkout.form.email')} htmlFor="checkout-email">
                <Input
                  id="checkout-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                />
              </FormField>
              <FormField label={t('billingUi:checkout.form.cpfCnpj')} htmlFor="checkout-cpf">
                <Input
                  id="checkout-cpf"
                  value={form.cpfCnpj}
                  onChange={(e) => setForm({ ...form, cpfCnpj: e.target.value })}
                  placeholder="000.000.000-00"
                />
              </FormField>
              <FormField label={t('billingUi:checkout.form.phone')} htmlFor="checkout-phone">
                <Input
                  id="checkout-phone"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="(11) 99999-9999"
                />
              </FormField>
            </div>

            <div>
              <p className="text-[10px] uppercase tracking-wider text-[var(--color-text-muted)] font-semibold mb-1.5">
                {t('billingUi:checkout.form.paymentMethod')}
              </p>
              <div className="grid grid-cols-3 gap-2">
                {(
                  [
                    { v: 'PIX', label: 'PIX', sub: t('billingUi:checkout.form.pixSub') },
                    { v: 'CREDIT_CARD', label: t('billingUi:checkout.form.cardLabel'), sub: t('billingUi:checkout.form.cardSub') },
                    { v: 'BOLETO', label: t('billingUi:checkout.form.boletoLabel'), sub: t('billingUi:checkout.form.boletoSub') },
                  ] as const
                ).map((m) => (
                  <button
                    key={m.v}
                    type="button"
                    onClick={() => setForm({ ...form, billingType: m.v })}
                    className={`rounded-lg border px-2 py-2.5 text-xs font-semibold transition-all flex flex-col items-center gap-0.5 ${
                      form.billingType === m.v
                        ? 'border-[var(--color-primary)] bg-[var(--color-primary)]/10 text-[var(--color-primary)]'
                        : 'border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text-muted)] hover:border-[var(--color-primary)]/40'
                    }`}
                  >
                    {m.label}
                    <span className="text-[9px] font-normal opacity-70">{m.sub}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Coupon / invite code — plans and storage packs */}
            <div>
              <p className="text-[10px] uppercase tracking-wider text-[var(--color-text-muted)] font-semibold mb-1.5">
                {t('billingUi:checkout.coupon.label')}
              </p>
                {appliedCoupon ? (
                  <div className="flex items-center justify-between gap-2 rounded-lg border border-[var(--color-accent)]/40 bg-[var(--color-accent)]/10 px-3 py-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <Ticket className="h-4 w-4 shrink-0 text-[var(--color-accent)]" />
                      <div className="min-w-0">
                        <span className="text-xs font-mono font-semibold text-[var(--color-accent)]">
                          {appliedCoupon.code}
                        </span>
                        {appliedCoupon.description && (
                          <span className="ml-2 text-[11px] text-[var(--color-text-muted)] truncate">
                            {appliedCoupon.description}
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setAppliedCoupon(null)}
                      aria-label={t('billingUi:checkout.coupon.remove')}
                      className="shrink-0 rounded-full p-1 text-[var(--color-text-muted)] hover:text-[var(--color-text)] hover:bg-[var(--color-surface-hover)] transition-colors"
                    >
                      <XIcon className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Input
                      value={couponInput}
                      onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                      placeholder={t('billingUi:checkout.coupon.placeholder')}
                      className="flex-1 font-mono uppercase"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleApplyCoupon();
                        }
                      }}
                    />
                    <Button
                      variant="outline"
                      onClick={handleApplyCoupon}
                      disabled={!couponInput.trim() || validateCouponMutation.isPending}
                    >
                      {validateCouponMutation.isPending ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        t('billingUi:checkout.coupon.apply')
                      )}
                    </Button>
                  </div>
                )}
            </div>

            {/* Summary */}
            <div className="rounded-lg bg-[var(--color-surface-elevated)] border border-[var(--color-border)] p-3 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-xs text-[var(--color-text-muted)]">
                  {sku.kind === 'pack' ? t('billingUi:checkout.summary.pack') : t('billingUi:checkout.summary.plan')}
                </div>
                <div
                  className="text-sm font-semibold truncate"
                  style={{ color: 'var(--color-text)' }}
                >
                  {sku.name}
                  {sku.kind === 'pack' && (
                    <span className="ml-2 text-[var(--color-accent)] text-xs font-mono">
                      +{formatStorage(sku.bonusBytes)}
                    </span>
                  )}
                </div>
              </div>
              <div className="text-right shrink-0">
                <div
                  className="text-xs"
                  style={{ color: 'var(--color-text-muted)' }}
                >
                  {t('billingUi:checkout.summary.total')}
                </div>
                <div
                  className="text-base font-bold tabular-nums"
                  style={{ color: 'var(--color-text)' }}
                >
                  {effectivePriceCents !== sku.priceCents && (
                    <span
                      className="mr-1.5 text-xs font-normal line-through"
                      style={{ color: 'var(--color-text-muted)' }}
                    >
                      {formatPriceBRL(sku.priceCents)}
                    </span>
                  )}
                  {formatPriceBRL(effectivePriceCents)}
                  <span
                    className="text-xs font-normal ml-0.5"
                    style={{ color: 'var(--color-text-muted)' }}
                  >
                    {t('billingUi:common.perMonth')}
                  </span>
                </div>
              </div>
            </div>

            <p className="text-[10px] text-[var(--color-text-muted)] text-center leading-relaxed">
              {t('billingUi:checkout.summary.recurringNote', { entity: sku.kind === 'pack' ? t('billingUi:checkout.summary.thePackEntity') : t('billingUi:checkout.summary.thePlanEntity') })}
            </p>
          </motion.div>
        )}

        {phase === 'submitting' && (
          <motion.div
            key="submitting"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex flex-col items-center justify-center py-12"
          >
            <BrandBar />
            <p className="mt-4 text-sm text-[var(--color-text-muted)]">
              {t('billingUi:checkout.phase.submittingTitle')}
            </p>
          </motion.div>
        )}

        {phase === 'card_form' && asaasCustomerId && (
          <motion.div
            key="card-form"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
          >
            <CreditCardForm
              email={form.email}
              cpfCnpj={form.cpfCnpj}
              phone={form.phone || undefined}
              onTokenGenerated={(token) => handleCardTokenized(token)}
              onError={() => {
                /* Toast already shown by form */
              }}
            />
          </motion.div>
        )}

        {phase === 'pix_qr' && pixResult && (
          <motion.div
            key="pix"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            className="space-y-4"
          >
            {pixResult.pixQrCode && (
              <div className="flex justify-center">
                <div className="rounded-2xl border border-[var(--color-border)] bg-white p-3 shadow-md">
                  <img
                    src={`data:image/png;base64,${pixResult.pixQrCode}`}
                    alt="QR PIX"
                    className="h-44 w-44"
                  />
                </div>
              </div>
            )}
            <FormField label={t('billingUi:checkout.pix.copyPasteLabel')} htmlFor="pix-copy">
              <div className="flex items-center gap-2">
                <input
                  id="pix-copy"
                  type="text"
                  value={pixResult.pixCopyPaste || ''}
                  readOnly
                  onFocus={(e) => e.currentTarget.select()}
                  className="flex-1 rounded-md border border-[var(--color-border)] bg-[var(--color-surface-elevated)] px-3 py-2 text-[11px] font-mono text-[var(--color-text)] truncate focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]/40"
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={copyPix}
                  className="border-[var(--color-border)] hover:bg-[var(--color-surface-hover)]"
                >
                  {pixCopied ? (
                    <Check className="h-4 w-4 text-[var(--color-accent)]" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </FormField>
            <p className="text-[11px] text-[var(--color-text-muted)] text-center leading-relaxed">
              {t('billingUi:checkout.pix.activationNote', { entity: sku.kind === 'pack' ? t('billingUi:checkout.summary.thePackEntity') : t('billingUi:checkout.summary.thePlanEntity') })}
            </p>
            {sku.kind === 'pack' && (
              <div className="rounded-lg bg-[var(--color-accent)]/10 border border-[var(--color-accent)]/30 p-3 flex items-center gap-2 text-[11px] text-[var(--color-accent)]">
                <ArrowRight className="h-3.5 w-3.5" />
                {t('billingUi:checkout.pix.afterConfirm')} <strong>+{formatStorage(sku.bonusBytes)}</strong> {t('billingUi:checkout.pix.released')}.
              </div>
            )}
          </motion.div>
        )}

        {phase === 'boleto_url' && boletoUrl && (
          <motion.div
            key="boleto"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            className="space-y-4"
          >
            <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface-elevated)] p-5 text-center">
              <Banknote className="h-10 w-10 text-[var(--color-primary)] mx-auto mb-3" />
              <p className="text-sm font-medium text-[var(--color-text)]">
                {t('billingUi:checkout.boleto.generatedTitle')}
              </p>
              <p className="text-xs text-[var(--color-text-muted)] mt-1">
                {t('billingUi:checkout.boleto.generatedSub')}
              </p>
            </div>
            <Button
              variant="primary"
              className="w-full"
              onClick={() => window.open(boletoUrl, '_blank', 'noopener')}
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              {t('billingUi:checkout.boleto.openButton')}
            </Button>
            <p className="text-[11px] text-[var(--color-text-muted)] text-center leading-relaxed">
              {t('billingUi:checkout.boleto.activationNote')}
            </p>
          </motion.div>
        )}

        {phase === 'cc_processing' && (
          <motion.div
            key="cc-processing"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex flex-col items-center justify-center py-12"
          >
            <BrandBar />
            <p className="mt-4 text-sm font-medium" style={{ color: 'var(--color-text)' }}>
              {t('billingUi:checkout.ccProcessing.title')}
            </p>
            <p className="mt-1 text-xs text-[var(--color-text-muted)] text-center max-w-xs leading-relaxed">
              {t('billingUi:checkout.ccProcessing.body')}
            </p>
          </motion.div>
        )}

        {phase === 'confirmed' && (
          <motion.div
            key="confirmed"
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ type: 'spring', stiffness: 320, damping: 22 }}
            className="flex flex-col items-center justify-center py-10"
          >
            <div className="h-16 w-16 rounded-full bg-[var(--color-accent)]/15 flex items-center justify-center">
              <Check className="h-8 w-8 text-[var(--color-accent)]" strokeWidth={3} />
            </div>
            <p className="mt-4 text-sm font-semibold" style={{ color: 'var(--color-text)' }}>
              {sku.kind === 'pack' ? t('billingUi:checkout.toast.packActivated') : t('billingUi:checkout.toast.planActivated')}
            </p>
            <p className="mt-1 text-xs text-[var(--color-text-muted)] text-center max-w-xs">
              {t('billingUi:checkout.toast.paymentConfirmed')}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </ResponsiveDialog>
  );
}
